import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActivity } from "@/context/ActivityContext";
import { UserPlus, UserMinus, Trash2, Users } from "lucide-react";

const LOCKED = ["iscrizione_chiusa", "in_corso", "terminata"];

export default function EnrollmentSection({ kind, id, state, enrollOverride }) {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { tick } = useActivity();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [myStatus, setMyStatus] = useState(null);
  const [autoEvents, setAutoEvents] = useState(true);
  const [myAuto, setMyAuto] = useState(true);
  const [savingAuto, setSavingAuto] = useState(false);

  const load = () => {
    api.get(`/${kind}/${id}/enrollments`).then((r) => setList(r.data)).catch(() => {});
    if (isAuthenticated) api.get(`/${kind}/${id}/my-enrollment`).then((r) => {
      setMyStatus(r.data.status);
      if (typeof r.data.auto_enroll_events === "boolean") setMyAuto(r.data.auto_enroll_events);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [kind, id, tick]); // eslint-disable-line

  const mine = myStatus === "approved" || (user && list.some((e) => e.user_id === user.id));
  const pending = myStatus === "pending";
  const locked = LOCKED.includes(state) && !enrollOverride && !isAdmin;

  const enroll = async () => {
    setBusy(true);
    try {
      const body = kind === "championships" ? { auto_enroll_events: autoEvents } : {};
      const r = await api.post(`/${kind}/${id}/enroll`, body);
      setMyStatus(r.data.status || "approved");
      toast.success(r.data.status === "pending" ? "Richiesta inviata: in attesa di approvazione" : "Iscrizione effettuata");
      load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const unenroll = async () => {
    setBusy(true);
    try { await api.delete(`/${kind}/${id}/enroll`); setMyStatus(null); toast.success("Iscrizione annullata"); load(); }
    catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };
  const del = async (eid) => { await api.delete(`/enrollments/${eid}`); load(); };
  const toggleMyAuto = async (enabled) => {
    setMyAuto(enabled);
    setSavingAuto(true);
    try {
      await api.put(`/${kind}/${id}/enroll/auto-events`, { enabled });
      toast.success(enabled ? "Iscrizione automatica ai Gran Premi attivata" : "Iscrizione automatica disattivata");
      load();
    } catch (e) {
      setMyAuto(!enabled);
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSavingAuto(false); }
  };
  const clearAll = async () => {
    if (!window.confirm("Cancellare tutti gli iscritti?")) return;
    await api.delete(`/${kind}/${id}/enrollments`); toast.success("Iscritti cancellati"); load();
  };

  return (
    <div data-testid="enrollment-section" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400">
          <Users className="h-4 w-4" /> Iscritti ({list.length})
        </span>
        {isAuthenticated && (pending ? (
          <div className="flex items-center gap-2">
            <span data-testid="enroll-pending-badge" className="text-xs font-bold uppercase tracking-wider text-amber-400 border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
              In attesa di approvazione
            </span>
            <button onClick={unenroll} disabled={busy} data-testid="cancel-request-btn"
              className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs font-bold uppercase tracking-wider px-4 py-2.5 transition-colors duration-200">
              <UserMinus className="h-4 w-4" /> Annulla richiesta
            </button>
          </div>
        ) : mine ? (
          <button onClick={unenroll} disabled={busy} data-testid="unenroll-btn"
            className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs font-bold uppercase tracking-wider px-4 py-2.5 transition-colors duration-200">
            <UserMinus className="h-4 w-4" /> Annulla iscrizione
          </button>
        ) : (
          <button onClick={enroll} disabled={busy || locked} data-testid="enroll-btn"
            className="inline-flex items-center gap-2 bg-yellow-400 text-zinc-950 disabled:opacity-40 text-xs font-bold uppercase tracking-wider px-4 py-2.5 hover:bg-yellow-300 transition-colors duration-200">
            <UserPlus className="h-4 w-4" /> Iscriviti
          </button>
        ))}
      </div>
      {kind === "championships" && isAuthenticated && !mine && !pending && (
        <label data-testid="auto-enroll-events-toggle"
          className="flex items-start gap-2.5 cursor-pointer border border-zinc-800 bg-zinc-900/50 p-3 -mt-1 select-none">
          <input type="checkbox" checked={autoEvents} onChange={(e) => setAutoEvents(e.target.checked)}
            data-testid="auto-enroll-events-checkbox"
            className="mt-0.5 h-4 w-4 accent-yellow-400 shrink-0" />
          <span className="text-xs text-zinc-300 leading-relaxed">
            <span className="font-bold text-zinc-100">Iscrizione automatica ai Gran Premi</span><br />
            Verrai iscritto in automatico a ogni Gran Premio (esistente e futuro) di questo campionato. Togli la spunta per iscriverti manualmente a ciascun Gran Premio.
          </span>
        </label>
      )}
      {kind === "championships" && isAuthenticated && mine && (
        <label data-testid="my-auto-enroll-toggle"
          className="flex items-start gap-2.5 cursor-pointer border border-zinc-800 bg-zinc-900/50 p-3 -mt-1 select-none">
          <input type="checkbox" checked={myAuto} disabled={savingAuto} onChange={(e) => toggleMyAuto(e.target.checked)}
            data-testid="my-auto-enroll-checkbox"
            className="mt-0.5 h-4 w-4 accent-yellow-400 shrink-0" />
          <span className="text-xs text-zinc-300 leading-relaxed">
            <span className="font-bold text-zinc-100">Iscrizione automatica ai Gran Premi</span><br />
            {myAuto
              ? "Sei iscritto in automatico a ogni Gran Premio di questo campionato. Togli la spunta per gestire le iscrizioni manualmente."
              : "Iscrizione automatica disattivata: iscriviti manualmente a ciascun Gran Premio. Riattiva la spunta per essere iscritto a tutti."}
          </span>
        </label>
      )}
      {!isAuthenticated && (
        <p className="text-xs text-zinc-500" data-testid="enroll-login-note">Accedi per iscriverti.</p>
      )}
      {locked && !mine && (
        <p className="text-xs text-amber-400" data-testid="enroll-locked-note">
          Iscrizioni chiuse. Chiedi all'amministratore di abilitarle.
        </p>
      )}
      {list.length === 0 ? (
        <p className="text-zinc-500 py-6 text-center text-sm" data-testid="no-enrollments">Nessun iscritto ancora.</p>
      ) : (
        <div className="border border-zinc-800" data-testid="enrollment-list">
          {list.map((e, i) => (
            <div key={e.id} className={`flex items-center justify-between px-5 py-3 border-t border-zinc-800 first:border-t-0 ${i % 2 ? "" : "bg-zinc-900/50"}`}>
              <span className="font-heading font-bold text-zinc-50">
                {e.name}{e.admin_added && <span className="ml-2 text-[10px] text-cyan-400">(admin)</span>}
              </span>
              {isAdmin && (
                <button onClick={() => del(e.id)} data-testid={`del-enroll-${i}`}
                  className="text-zinc-500 hover:text-red-400 transition-colors duration-200">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {isAdmin && list.length > 0 && (
        <button onClick={clearAll} data-testid="clear-enrollments-btn"
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-red-400 hover:text-red-300 transition-colors duration-200">
          <Trash2 className="h-3.5 w-3.5" /> Cancella tutti gli iscritti
        </button>
      )}
    </div>
  );
}
